/**
 * AgentContext — Bridge tour state + workflow canvas state
 * vào một global window object that the system prompt builder consumes.
 *
 * Page Agent runs in-page; it cannot read React state directly. Publishing
 * to `window.__TADA_AGENT_CTX__` keeps the bridge framework-agnostic and
 * also lets you inspect the live context from DevTools.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkflowStore } from '../../pages/WorkflowBuilder/store';

export interface AgentContext {
  currentPage: string;          // pathname
  tourActive: boolean;
  tourStepId: string | null;    // current step.id
  tourStepIndex: number;
  tourPhase: number | null;
  workflowName: string;
  workflowNodes: Array<{ id: string; type: string }>;
  workflowEdges: number;
  language: string;
}

declare global {
  interface Window {
    __TADA_AGENT_CTX__?: AgentContext;
  }
}

const DEFAULT: AgentContext = {
  currentPage: '/',
  tourActive: false,
  tourStepId: null,
  tourStepIndex: 0,
  tourPhase: null,
  workflowName: '',
  workflowNodes: [],
  workflowEdges: 0,
  language: 'vi',
};

/** Read the latest agent context (consumed by AgentChatButton when sending). */
export function getAgentContext(): AgentContext {
  return window.__TADA_AGENT_CTX__ || { ...DEFAULT };
}

/**
 * Hook to keep window.__TADA_AGENT_CTX__ in sync with React state.
 * Mount once near the root (after WorkflowTourProvider, inside Router).
 */
export function useAgentContextSync(params: {
  tourActive: boolean;
  tourStepId: string | null;
  tourStepIndex: number;
  tourPhase: number | null;
  language?: string;
}): void {
  const location = useLocation();
  // Use separate selectors — avoids creating a new object on every render
  // which would cause Zustand to always see a changed reference → infinite loop.
  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);
  const workflowName = useWorkflowStore((s) => s.workflowName);

  const { tourActive, tourStepId, tourStepIndex, tourPhase, language } = params;

  useEffect(() => {
    window.__TADA_AGENT_CTX__ = {
      currentPage: location.pathname,
      tourActive,
      tourStepId,
      tourStepIndex,
      tourPhase,
      workflowName,
      workflowNodes: nodes
        .filter((n) => n.type !== 'data_zone')
        .map((n) => ({ id: n.id, type: n.type || 'unknown' })),
      workflowEdges: edges.length,
      language: language || 'vi',
    };
  }, [
    location.pathname,
    tourActive,
    tourStepId,
    tourStepIndex,
    tourPhase,
    language,
    nodes,
    edges,
    workflowName,
  ]);
}
